// The alert destinations a tenant has configured.
//
// One Panel each rather than table rows: a destination carries a failure
// sentence and its recovery action, which is two lines of prose that a cell
// cannot hold. A tenant can have at most 25, so nothing here needs
// virtualizing or a column picker.

import Button from "../ui/Button.jsx";
import Panel from "../ui/Panel.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import { SkeletonBar } from "../ui/Skeleton.jsx";
import { describeRoutes } from "../../lib/pushNotificationRoutes.js";
import { failureCopy } from "../../lib/pushNotificationErrors.js";
import { PUSH_VENDORS, vendorByWire } from "../../lib/pushNotificationVendors.js";

/** Every vendor, as prose: "A", "A or B", "A, B or C". */
export function vendorNames() {
    const labels = PUSH_VENDORS.map((v) => v.label);
    if (labels.length <= 1) return labels.join("");
    return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

/** How a destination's batching window reads on the card. */
function describeWindow(integration) {
    const set = integration.windowSecondsSet ?? integration.window_seconds_set ?? false;
    if (!set) return "every minute";
    const seconds = Number(integration.windowSeconds ?? integration.window_seconds ?? 0);
    if (seconds < 60) return `every ${seconds} seconds`;
    const minutes = Math.round(seconds / 60);
    return `every ${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * A destination's state as one word and one colour.
 *
 * Keyed on `lastError`, not on the failure streak: a destination rejected
 * before we ever send -- an unreadable credential, a URL we refuse -- never
 * advances the streak, and used to read as "nothing delivered yet".
 */
function statusOf(integration) {
    const lastError = integration.lastError ?? integration.last_error ?? "";
    if (!integration.enabled) return { label: "Paused", dot: "var(--muted-soft)", lastError: "" };
    if (lastError) return { label: "Failing", dot: "var(--danger)", lastError };
    return { label: "Delivering", dot: "var(--green)", lastError: "" };
}

function DestinationCard({ integration, signalNamesById, canManage, onToggle, onRemove }) {
    const spec = vendorByWire(integration.vendor);
    const state = statusOf(integration);
    const failure = state.lastError ? failureCopy(state.lastError) : null;
    const lastDelivery = integration.lastDeliveryAt ?? integration.last_delivery_at ?? null;
    const lastFailure = integration.lastFailureAt ?? integration.last_failure_at ?? null;

    return (
        <Panel
            as="li"
            title={
                <span className="flex min-w-0 items-center gap-[8px]">
                    <span aria-hidden="true" className="h-[8px] w-[8px] shrink-0 rounded-full" style={{ background: state.dot }} />
                    <span className="min-w-0 truncate">{integration.displayName ?? integration.display_name}</span>
                    <span className="shrink-0 text-[12.5px] font-normal text-[color:var(--muted)]">{state.label}</span>
                </span>
            }
            meta={
                <>
                    <span className="block">
                        {spec?.label ?? "Unknown"}
                        {", sends "}
                        {describeRoutes(integration.routes, signalNamesById)}
                        {" "}
                        {describeWindow(integration)}
                    </span>
                    {/* The only positive signal on the card: without it a
                        working destination and one that has never delivered
                        look identical. */}
                    <span className="block">
                        {lastDelivery
                            ? `Last delivered ${new Date(lastDelivery).toLocaleString()}`
                            : "Nothing delivered yet"}
                    </span>
                </>
            }
            actions={
                <>
                    <Button size="sm" onClick={() => onToggle(integration)}>
                        {integration.enabled ? "Pause" : "Resume"}
                    </Button>
                    {canManage ? (
                        <Button size="sm" variant="danger" onClick={() => onRemove(integration)}>
                            Remove
                        </Button>
                    ) : null}
                </>
            }
        >
            {/* The recovery mechanism, not a footnote: retries span seconds, so
                a failed delivery is not coming later. For a destination the
                customer configured, the customer is the only person who can fix
                it -- so the reason and the time are on screen rather than only
                in our metrics. */}
            {failure ? (
                <ErrorText>
                    {failure.summary}
                    {lastFailure ? ` (last tried ${new Date(lastFailure).toLocaleString()})` : ""}.{" "}
                    {failure.action}
                </ErrorText>
            ) : null}
        </Panel>
    );
}

/** Panel-shaped placeholders, the height of a DestinationCard, while the list loads. */
function LoadingCards({ count = 2 }) {
    return (
        <div data-destinations-loading="">
            <span className="sr-only">Loading destinations…</span>
            <ul aria-hidden="true" className="m-0 grid list-none gap-[8px] p-0">
                {Array.from({ length: count }, (_, i) => (
                    <Panel as="li" key={i} className="h-[89px] content-center" style={{ opacity: 1 - i * 0.35 }}>
                        <div className="grid gap-[9px]">
                            <SkeletonBar className="w-[36%]" />
                            <SkeletonBar className="w-[62%]" />
                            <SkeletonBar className="w-[28%]" />
                        </div>
                    </Panel>
                ))}
            </ul>
        </div>
    );
}

// The destinations as cards; the panel's Listing says when there are none.
export default function DestinationList({
    integrations,
    loading = false,
    signalNamesById,
    canManage,
    onToggle,
    onRemove,
}) {
    if (loading) return <LoadingCards />;
    return (
        <ul className="m-0 grid list-none gap-[8px] p-0">
            {integrations.map((integration) => (
                <DestinationCard
                    key={integration.id}
                    integration={integration}
                    signalNamesById={signalNamesById}
                    canManage={canManage}
                    onToggle={onToggle}
                    onRemove={onRemove}
                />
            ))}
        </ul>
    );
}
